/**
 * Payments — Stage 8.
 *
 * Two backend feeds power this page:
 * - `/api/bi/payments/funnel/`   gateway initiation funnel + daily trend
 * - `/api/bi/payments/mandates/` R2P mandate lifecycle aggregates
 *
 * The payments domain is small and store-agnostic, so the global store
 * filter is intentionally ignored here — we don't want a single-store
 * picker to silently zero out a finance dashboard.
 */
import { useMemo, useState } from 'react';
import {
  Sparkles,
  Wallet,
  Banknote,
  ShieldCheck,
} from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { KPICard } from '@/components/charts/KPICard';
import { Sparkline } from '@/components/charts/Sparkline';
import { DataView } from '@/components/data/DataView';
import {
  usePaymentsFunnel,
  usePaymentsMandates,
  type PaymentsGatewayRow,
  type MandateBankRow,
  type MandateExpiringRow,
} from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

export function PaymentsPage() {
  const range = useFilters((s) => s.range);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );
  const [tab, setTab] = useState<'funnel' | 'mandates'>('funnel');

  return (
    <>
      <PageHeader
        eyebrow="Finance"
        title="Payments"
        description="Where the money tries to enter, where it actually lands, and which mandates are quietly aging out."
        actions={
          <Badge tone="info" className="gap-1">
            <Sparkles className="h-3 w-3" /> Stage 8
          </Badge>
        }
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'funnel' | 'mandates')} className="mt-4">
        <TabsList>
          <TabsTrigger value="funnel" className="gap-1.5">
            <Wallet className="h-3.5 w-3.5" /> Gateway funnel
          </TabsTrigger>
          <TabsTrigger value="mandates" className="gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" /> Mandates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="funnel" className="mt-4 focus-visible:outline-none">
          <FunnelTab start={startISO} end={endISO} />
        </TabsContent>
        <TabsContent value="mandates" className="mt-4 focus-visible:outline-none">
          <MandatesTab start={startISO} end={endISO} />
        </TabsContent>
      </Tabs>
    </>
  );
}

function FunnelTab({ start, end }: { start: string; end: string }) {
  const query = usePaymentsFunnel({ start, end });
  const data = query.data;
  const totals = data?.totals;
  const successTrend = useMemo(
    () => (data?.daily ?? []).map((d) => ({ value: d.success_rate * 100 })),
    [data?.daily],
  );
  const initTrend = useMemo(
    () => (data?.daily ?? []).map((d) => ({ value: d.initiations })),
    [data?.daily],
  );

  const columns = useMemo<ColumnDef<PaymentsGatewayRow, unknown>[]>(
    () => [
      {
        id: 'gateway',
        header: 'Gateway',
        accessorKey: 'gateway',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium text-foreground">{row.original.gateway}</span>
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {row.original.type}
            </span>
          </div>
        ),
      },
      {
        id: 'initiations',
        header: 'Initiations',
        accessorKey: 'initiations',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.number(getValue<number>())}</span>,
      },
      {
        id: 'successes',
        header: 'Successes',
        accessorKey: 'successes',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.number(getValue<number>())}</span>,
      },
      {
        id: 'success_rate',
        header: 'Success rate',
        accessorKey: 'success_rate',
        cell: ({ getValue }) => {
          const v = getValue<number>();
          const tone = v >= 0.9 ? 'success' : v >= 0.7 ? 'info' : v >= 0.5 ? 'warning' : 'danger';
          return <Badge tone={tone}>{fmt.pct(v)}</Badge>;
        },
      },
      {
        id: 'conversion_rate',
        header: 'Order conversion',
        accessorKey: 'conversion_rate',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.pct(getValue<number>())}</span>,
      },
      {
        id: 'amount',
        header: 'Amount initiated',
        accessorKey: 'amount',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.npr(getValue<number>())}</span>,
      },
      {
        id: 'unique_users',
        header: 'Unique users',
        accessorKey: 'unique_users',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.number(getValue<number>())}</span>,
      },
    ],
    [],
  );

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Initiations"
          value={fmt.number(totals?.initiations ?? 0)}
          loading={query.isLoading}
          trend={initTrend}
        />
        <KPICard
          label="Success rate"
          value={fmt.pct(totals?.success_rate ?? 0)}
          loading={query.isLoading}
          trend={successTrend}
        />
        <KPICard
          label="Order conversion"
          value={fmt.pct(totals?.conversion_rate ?? 0)}
          loading={query.isLoading}
          hint="Successful initiations whose cart became an order."
        />
        <KPICard
          label="Amount initiated"
          value={fmt.npr(totals?.amount ?? 0)}
          loading={query.isLoading}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Daily success %</CardTitle>
            <Badge tone="neutral">{(totals?.successes ?? 0).toLocaleString()} ok</Badge>
          </CardHeader>
          <CardBody>
            {query.isLoading ? (
              <div className="h-32 animate-pulse rounded-md bg-muted/40" />
            ) : successTrend.length === 0 ? (
              <p className="text-sm text-muted-foreground">No initiations in this window.</p>
            ) : (
              <Sparkline data={successTrend} tone="success" height={120} />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top failure codes</CardTitle>
            <Badge tone={(data?.failures.length ?? 0) > 0 ? 'warning' : 'neutral'}>
              {(data?.failures.length ?? 0)} codes
            </Badge>
          </CardHeader>
          <CardBody>
            {query.isLoading ? (
              <div className="h-32 animate-pulse rounded-md bg-muted/40" />
            ) : (data?.failures ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No non-200 codes recorded.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {(data?.failures ?? []).slice(0, 8).map((f) => (
                  <li key={f.status_code} className="flex items-center justify-between">
                    <span className="font-mono text-xs text-muted-foreground">HTTP {f.status_code}</span>
                    <span className="kpi-num text-foreground">{fmt.number(f.count)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        {query.isError ? (
          <ErrorState description="Couldn't load payments funnel." onRetry={() => void query.refetch()} />
        ) : (
          <DataView<PaymentsGatewayRow>
            title="By gateway"
            data={data?.gateways ?? []}
            columns={columns}
            isLoading={query.isLoading}
            emptyTitle="No initiations in this window"
          />
        )}
      </div>
    </>
  );
}

function MandatesTab({ start, end }: { start: string; end: string }) {
  const query = usePaymentsMandates({ start, end });
  const data = query.data;
  const totals = data?.totals;
  const newDaily = useMemo(
    () => (data?.daily ?? []).map((d) => ({ value: d.created })),
    [data?.daily],
  );
  const amountDaily = useMemo(
    () => (data?.daily ?? []).map((d) => ({ value: d.amount })),
    [data?.daily],
  );

  const bankColumns = useMemo<ColumnDef<MandateBankRow, unknown>[]>(
    () => [
      {
        id: 'bank',
        header: 'Bank',
        accessorKey: 'bank_name',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium text-foreground">{row.original.bank_name}</span>
            <span className="text-[11px] font-mono text-muted-foreground">
              {row.original.bank_id}
            </span>
          </div>
        ),
      },
      {
        id: 'count',
        header: 'New mandates',
        accessorKey: 'count',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.number(getValue<number>())}</span>,
      },
      {
        id: 'active',
        header: 'Active',
        accessorKey: 'active',
        cell: ({ getValue }) => (
          <Badge tone={getValue<number>() > 0 ? 'success' : 'neutral'}>
            {fmt.number(getValue<number>())}
          </Badge>
        ),
      },
      {
        id: 'amount',
        header: 'Amount',
        accessorKey: 'amount',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.npr(getValue<number>())}</span>,
      },
    ],
    [],
  );

  const expColumns = useMemo<ColumnDef<MandateExpiringRow, unknown>[]>(
    () => [
      {
        id: 'bank',
        header: 'Bank',
        accessorKey: 'bank_name',
        cell: ({ getValue }) => <span className="font-medium text-foreground">{getValue<string>()}</span>,
      },
      {
        id: 'amount',
        header: 'Amount',
        accessorKey: 'amount',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.npr(getValue<number>())}</span>,
      },
      {
        id: 'mobile',
        header: 'Mobile',
        accessorKey: 'mobile_no',
        cell: ({ getValue }) => <span className="font-mono text-xs">{getValue<string>()}</span>,
      },
      {
        id: 'expires',
        header: 'Expires',
        accessorKey: 'expires',
        cell: ({ getValue }) => {
          const d = getValue<string | null>();
          if (!d) return <span className="text-muted-foreground">—</span>;
          return (
            <Badge tone={daysUntil(d) <= 14 ? 'danger' : 'warning'}>
              {fmt.date(d)} ({daysUntil(d)}d)
            </Badge>
          );
        },
      },
    ],
    [],
  );

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Lifetime mandates"
          value={fmt.number(totals?.lifetime ?? 0)}
          loading={query.isLoading}
        />
        <KPICard
          label="Active"
          value={fmt.number(totals?.active ?? 0)}
          loading={query.isLoading}
          hint={`${fmt.pct(totals?.active_rate ?? 0)} of lifetime`}
        />
        <KPICard
          label="New in window"
          value={fmt.number(totals?.new_in_window ?? 0)}
          loading={query.isLoading}
          trend={newDaily}
        />
        <KPICard
          label="Failed"
          value={fmt.number(totals?.failed ?? 0)}
          goodWhenUp={false}
          loading={query.isLoading}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Status mix</CardTitle>
          </CardHeader>
          <CardBody>
            {query.isLoading ? (
              <div className="h-28 animate-pulse rounded-md bg-muted/40" />
            ) : (data?.status_lifetime ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No mandates yet.</p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {(data?.status_lifetime ?? []).map((s) => (
                  <li key={s.status} className="flex items-center justify-between">
                    <Badge tone={statusTone(s.status)}>{s.status}</Badge>
                    <span className="kpi-num text-foreground">{fmt.number(s.count)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>New mandate value (daily)</CardTitle>
            <Banknote className="h-3.5 w-3.5 text-muted-foreground" />
          </CardHeader>
          <CardBody>
            {query.isLoading ? (
              <div className="h-32 animate-pulse rounded-md bg-muted/40" />
            ) : amountDaily.length === 0 ? (
              <p className="text-sm text-muted-foreground">No new mandates in this window.</p>
            ) : (
              <Sparkline data={amountDaily} tone="primary" height={120} />
            )}
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        {query.isError ? (
          <ErrorState
            description="Couldn't load mandate data."
            onRetry={() => void query.refetch()}
          />
        ) : (
          <DataView<MandateBankRow>
            title="By bank (window)"
            data={data?.banks ?? []}
            columns={bankColumns}
            isLoading={query.isLoading}
            emptyTitle="No new mandates this window"
          />
        )}
      </div>

      {(data?.expiring_soon ?? []).length > 0 ? (
        <div className="mt-4">
          <DataView<MandateExpiringRow>
            title="Expiring within 60 days"
            data={data?.expiring_soon ?? []}
            columns={expColumns}
            isLoading={false}
            emptyTitle="Nothing expires soon"
          />
        </div>
      ) : null}
    </>
  );
}

function daysUntil(iso: string): number {
  const target = new Date(iso).getTime();
  const today = Date.now();
  return Math.max(0, Math.round((target - today) / 86_400_000));
}

function statusTone(status: string): 'success' | 'danger' | 'warning' | 'neutral' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'FAILED':
      return 'danger';
    case 'PENDING':
      return 'warning';
    default:
      return 'neutral';
  }
}

// Reserved for the unused-import lint suppressor.
export const _classes = cn;
